import React from 'react'
import styled from '@emotion/styled'
import { Typography } from 'src/components/atoms/Typography'
import { BreakPoint, Styles } from '../shared/CssValues'
import { NoLineBreak } from '../atoms/NoLineBreak'
import { FluidObject } from 'gatsby-image'
import { Image } from 'src/components/atoms/Image'
import { vwSize } from 'src/utils/vwCss'
// image
import checkIcon from 'src/images/top/icon_check.svg'

type SpVariant = 'left' | 'center'

type Props = {
  imageFluid?: FluidObject
  imageUrl?: string
  title: string
  description: string
  spVariant?: SpVariant
  number: string
  feature?: []
}

type StyledProps = {
  spVariant: SpVariant
}

/**
 * 画像・タイトル・説明のひとかたまり
 */
export const PointCard: React.VFC<Props> = ({
  imageFluid,
  imageUrl,
  title,
  description,
  spVariant = 'center',
  number = false,
  feature = []
}) => {
  return (
    <Card spVariant={spVariant}>
      {number &&
        <PointNumber>{number}</PointNumber>
      }
      {imageFluid ? (
        <RasterImageWrapper spVariant={spVariant}>
          <RasterImage imageFluid={imageFluid} />
        </RasterImageWrapper>
      ) : (
        <VectorImage src={imageUrl} alt="" />
      )}
      <Contents spVariant={spVariant}>
        <Title as="h3" className={"g-h3 -vw20"} spVariant={spVariant}>
          <div>
            {title.split('\n').map(str => (
              <NoLineBreak key={str}>{str}<br className={"pc-none"} /></NoLineBreak>
            ))}
          </div>
        </Title>
        {description &&
          <Description spVariant={spVariant}>{description}</Description>
        }
        {feature &&
          <FeatureWrap>
          {feature.map(f => {
            return (
              <FeatureOuter>
                <Feature>
                  <FeatureText>{f}</FeatureText>
                </Feature>
              </FeatureOuter>
            )
          })}
          </FeatureWrap>
        }
      </Contents>
    </Card>
  )
}

const Card = styled.div<StyledProps>`
  display: block;
  width: ${vwSize(330, 'pc')}vw;
  @media screen and (max-width: ${BreakPoint.MOBILE_L}) {
    width: 100%;
    margin-bottom: ${vwSize(64, 'sp')}vw;
  }
  &:lst-child {
    @media screen and (max-width: ${BreakPoint.MOBILE_L}) {
        margin-bottom: 0;
      }
  }
`

const RasterImageWrapper = styled.div<StyledProps>`
  height: auto;
  align-self: flex-start;
  width: 100%;
  @media screen and (max-width: ${BreakPoint.MOBILE_L}) {
    margin-top: ${vwSize(24, 'sp')}vw;
  }
`

const RasterImage = styled(Image)``

const VectorImage = styled.img`
  position: relative;
  width: ${vwSize(280, 'pc')}vw;
  margin: -${vwSize(30, 'pc')}vw auto 0;
  display: block;
  @media screen and (max-width: ${BreakPoint.MOBILE_L}) {
    width: ${vwSize(180, 'sp')}vw;
    margin: -${vwSize(20, 'sp')}vw auto 0;
  }
`

const Contents = styled.div<StyledProps>`
  margin-top: ${vwSize(20, 'pc')}vw;
`

const Title = styled(Typography)<StyledProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 4em;
  box-sizing: border-box;
  text-align: center;
  @media screen and (max-width: ${BreakPoint.MOBILE_L}) {
    height: auto;
  }
`

const Description = styled(Typography)<StyledProps>`
  margin: ${({ spVariant }) =>
    spVariant === 'center'
      ? `${Styles.spacing[7]} 0 0`
      : `${Styles.spacing[6]} 0 0`};
  font-size: ${Styles.fontSize[300]};
  line-height: ${Styles.lineHeight[400]};
  letter-spacing: ${Styles.letterSpacing[400]};
  @media screen and (min-width: ${BreakPoint.TABLET}) {
    font-size: ${Styles.fontSize[400]};
    margin: ${vwSize(16, 'sp')}vw 0 0;
  }
`
const FeatureWrap = styled.div`
  margin-top: ${vwSize(16, 'pc')}vw;
  @media screen and (max-width: ${BreakPoint.MOBILE_L}) {
    margin-top: ${vwSize(16, 'sp')}vw;
  }
`
const FeatureOuter = styled.div`
  box-sizing: border-box;
  display: flex;
  justify-content: center;
`
const Feature = styled.div`
  box-sizing: border-box;
  margin-bottom: ${vwSize(8, 'pc')}vw;
  @media screen and (max-width: ${BreakPoint.MOBILE_L}) {
    display: flex;
    justify-content: center;
    margin-bottom: ${vwSize(8, 'sp')}vw;
  }
`
const FeatureText = styled(Typography)`
  color: ${Styles.color.primary[400]};
  text-align: left;
  position: relative;
  box-sizing: border-box;
  display: inline-block;
  background: ${Styles.color.primary[200]};
  padding: ${vwSize(16, 'pc')}vw ${vwSize(8, 'pc')}vw ${vwSize(16, 'pc')}vw ${vwSize(42, 'pc')}vw;
  border-radius: ${Styles.pc.borderRadius};
  white-space: nowrap;
  letter-spacing: ${Styles.pc.letterSpacing};
  font-size: ${vwSize(16, 'pc')}vw;

  @media screen and (max-width: ${BreakPoint.MOBILE_L}) {
    padding: ${vwSize(8, 'sp')}vw ${vwSize(16, 'sp')}vw ${vwSize(8, 'sp')}vw ${vwSize(32, 'sp')}vw;
    border-radius: ${Styles.sp.borderRadius};
    font-size: ${vwSize(16, 'sp')}vw;
  }

  &:before {
    content: "";
    background-image: url(${checkIcon});
    background-repeat: no-repeat;
    background-position: center center;
    background-size: contain;
    width: ${vwSize(16, 'pc')}vw;
    height: ${vwSize(16, 'pc')}vw;
    position: absolute;
    left: ${vwSize(16, 'pc')}vw;
    top: 50%;
    transform: translateY(-50%);
    @media screen and (max-width: ${BreakPoint.MOBILE_L}) {
      width: ${vwSize(16, 'sp')}vw;
      height: ${vwSize(16, 'sp')}vw;
      left: ${vwSize(12, 'sp')}vw;
    }
  }
`
const PointNumber = styled(Typography)`
  color: ${Styles.color.secondary[200]};
  font-weight: bold;
  font-size: ${vwSize(70, 'pc')}vw;
  @media screen and (max-width: ${BreakPoint.MOBILE_L}) {
    font-size: ${vwSize(48, 'sp')}vw;
    padding-left: ${vwSize(45, 'sp')}vw;
  }
`
